import { useTranslation } from "@privacy/translations";
import { IconButton, Box, ToggleButton, useMediaQuery } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useSearchParams } from "react-router";
import { type IconProps, ChevronDownIcon } from "../shared/icons";
import { trackEvent } from "../shared/Snowplow";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";

export type SidePanelGroup = {
  renderTabIcon: (props: IconProps) => ReactNode;
  tabLabel: string;
  mainPanelKey: string;
  subPanelKeys: string[];
  panelWidth?: string;
};

export type SidePanelProps = {
  panelGroups: SidePanelGroup[];
  renderContent: (props: { activePanelKey: string; activePanelGroup: SidePanelGroup }) => ReactNode;
};

const PANEL_PARAM_KEY = "side-panel";

export const SidePanelTabs: FC<{ panelGroups: SidePanelGroup[] }> = ({ panelGroups }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hideTabs = useMediaQuery("(max-width: 1150px)");

  const activePanelKey = searchParams.get(PANEL_PARAM_KEY) ?? "";

  if (hideTabs || panelGroups.length === 0) return null;

  const handleTabClick = (newValue: string, isPrevSelected: boolean) => {
    if (isPrevSelected) {
      searchParams.delete(PANEL_PARAM_KEY);
    } else {
      searchParams.set(PANEL_PARAM_KEY, newValue);
      trackEvent.clickSidePanelTab(newValue);
    }
    setSearchParams(searchParams);
  };

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {panelGroups.map(panelGroup => {
        const isSelected =
          activePanelKey === panelGroup.mainPanelKey || panelGroup.subPanelKeys.includes(activePanelKey);
        return (
          <ToggleButton
            onChange={(_, newValue) => handleTabClick(newValue, isSelected)}
            key={panelGroup.mainPanelKey}
            value={panelGroup.mainPanelKey}
            selected={isSelected}
          >
            {panelGroup.renderTabIcon({ size: 14, color: theme => theme.palette.text.secondary })}
            <HACK_alignIconWithLabelBox>{panelGroup.tabLabel}</HACK_alignIconWithLabelBox>
          </ToggleButton>
        );
      })}
    </Box>
  );
};

export const SidePanel: FC<SidePanelProps> = props => {
  const [searchParams] = useSearchParams();

  const activePanelKey = searchParams.get(PANEL_PARAM_KEY) ?? "";

  if (!activePanelKey) return null;

  const activePanelGroup = props.panelGroups.find(
    group => group.mainPanelKey === activePanelKey || group.subPanelKeys.includes(activePanelKey)
  );

  if (!activePanelGroup) return null;

  return (
    <SidePanelWrapper width={activePanelGroup.panelWidth}>
      {props.renderContent({ activePanelKey, activePanelGroup })}
    </SidePanelWrapper>
  );
};

export const SubSidePanelBackButton: FC<{ panelGroup: SidePanelGroup }> = ({ panelGroup }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleBackClick = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(PANEL_PARAM_KEY, panelGroup.mainPanelKey);
    setSearchParams(newParams);
  };

  return (
    <IconButton onClick={handleBackClick} aria-label={t("GridDrawer.SubDrawer.Back")}>
      <ChevronDownIcon rotate={90} size={14} />
    </IconButton>
  );
};

const SidePanelWrapper = ({ children, width = "500px" }: { children: ReactNode; width?: string }) => {
  return (
    <Box
      sx={{
        height: "100%",
        pt: 3,
        width,
        backgroundColor: theme => theme.palette.grey["50"],
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      {children}
    </Box>
  );
};

export const useGoToSidePanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  return (newValue: string | null) => {
    newValue ? searchParams.set(PANEL_PARAM_KEY, newValue) : searchParams.delete(PANEL_PARAM_KEY);
    setSearchParams(searchParams);
  };
};
